import { useState } from "react";
import {
  useCreatePointsConfiguration,
  useUpdatePointsConfiguration,
} from "../hooks/usePointsConfig";
import type {
  PointsConfiguration,
  PointsConditionOperator,
  PointsConditionType,
  PointsConfigurationConditionInput,
  PointsEarnBasis,
} from "../services/pointsConfigApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNotification } from "@/shared/hooks/useNotification";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing?: PointsConfiguration | null;
}

const CONDITION_TYPES: PointsConditionType[] = [
  "CustomerGroup",
  "ProductCategory",
  "OrderAmount",
  "OrderType",
  "CampaignPeriod",
];
const OPERATORS: PointsConditionOperator[] = [
  "Equals",
  "GreaterThanOrEqual",
  "LessThanOrEqual",
  "In",
  "Between",
];

const toDateInput = (iso?: string | null) => (iso ? iso.slice(0, 10) : "");

/**
 * Wrapper that keeps the Dialog mounted (for animations) but only mounts the
 * form body while open, keyed by the config id so its fields initialise fresh
 * from props via useState initialisers — no setState-in-effect.
 */
export const PointsConfigFormDialog = ({ open, onOpenChange, existing }: Props) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
      {open && (
        <PointsConfigFormBody
          key={existing?.id ?? "new"}
          existing={existing ?? null}
          onClose={() => onOpenChange(false)}
        />
      )}
    </DialogContent>
  </Dialog>
);

const PointsConfigFormBody = ({
  existing,
  onClose,
}: {
  existing: PointsConfiguration | null;
  onClose: () => void;
}) => {
  const notification = useNotification();
  const createMutation = useCreatePointsConfiguration();
  const updateMutation = useUpdatePointsConfiguration();
  const isEdit = !!existing;

  const [name, setName] = useState(existing?.name ?? "");
  // v1 always earns on order amount; category/group are expressed as Conditions.
  // EarnBasis is retained in the model for future earning bases.
  const [earnBasis] = useState<PointsEarnBasis>(existing?.earnBasis ?? "OrderAmount");
  const [earnRatePoints, setEarnRatePoints] = useState(String(existing?.earnRatePoints ?? 1));
  const [earnRatePerAmount, setEarnRatePerAmount] = useState(String(existing?.earnRatePerAmount ?? 100));
  const [pointValueKes, setPointValueKes] = useState(String(existing?.pointValueKes ?? 1));
  const [minRedeemable, setMinRedeemable] = useState(String(existing?.minRedeemablePoints ?? 100));
  const [maxRedeemable, setMaxRedeemable] = useState(
    existing?.maxRedeemablePointsPerOrder != null ? String(existing.maxRedeemablePointsPerOrder) : ""
  );
  const [effectiveFrom, setEffectiveFrom] = useState(
    toDateInput(existing?.effectiveFrom) || new Date().toISOString().slice(0, 10)
  );
  const [effectiveTo, setEffectiveTo] = useState(toDateInput(existing?.effectiveTo));
  const [isActiveFlag, setIsActiveFlag] = useState(existing?.isActiveFlag ?? true);
  const [conditions, setConditions] = useState<PointsConfigurationConditionInput[]>(
    (existing?.conditions ?? []).map((c) => ({
      conditionType: c.conditionType,
      operator: c.operator,
      value: c.value,
    }))
  );

  const mutation = isEdit ? updateMutation : createMutation;

  // Required/validation rules mirror the backend ValidateRule. Fields with an
  // error get aria-invalid (red border) and the submit button stays disabled
  // until every rule passes.
  const errors = {
    name: name.trim() ? undefined : "Configuration name is required.",
    pointValueKes: Number(pointValueKes) > 0 ? undefined : "Point value must be greater than zero.",
    earnRatePoints: Number(earnRatePoints) >= 0 ? undefined : "Cannot be negative.",
    earnRatePerAmount: Number(earnRatePerAmount) >= 0 ? undefined : "Cannot be negative.",
    minRedeemable: Number(minRedeemable) >= 0 ? undefined : "Cannot be negative.",
    maxRedeemable:
      maxRedeemable.trim() && Number(maxRedeemable) < Number(minRedeemable)
        ? "Max cannot be less than the minimum redeemable points."
        : undefined,
    effectiveFrom: effectiveFrom ? undefined : "Effective-from date is required.",
    effectiveTo:
      effectiveTo && effectiveFrom && new Date(effectiveTo) < new Date(effectiveFrom)
        ? "Effective-to cannot be before effective-from."
        : undefined,
  };
  const isValid = !Object.values(errors).some(Boolean);

  const addCondition = () =>
    setConditions((prev) => [...prev, { conditionType: "CustomerGroup", operator: "Equals", value: "" }]);
  const removeCondition = (idx: number) => setConditions((prev) => prev.filter((_, i) => i !== idx));
  const updateCondition = (idx: number, patch: Partial<PointsConfigurationConditionInput>) =>
    setConditions((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));

  const submit = () => {
    if (!name.trim()) {
      notification.error("Configuration name is required.");
      return;
    }
    if (Number(pointValueKes) <= 0) {
      notification.error("Point value must be greater than zero.");
      return;
    }
    if (!effectiveFrom) {
      notification.error("Effective-from date is required.");
      return;
    }

    const payload = {
      name: name.trim(),
      earnBasis,
      earnRatePoints: Number(earnRatePoints) || 0,
      earnRatePerAmount: Number(earnRatePerAmount) || 0,
      pointValueKes: Number(pointValueKes) || 0,
      minRedeemablePoints: Number(minRedeemable) || 0,
      maxRedeemablePointsPerOrder: maxRedeemable.trim() ? Number(maxRedeemable) : null,
      effectiveFrom: new Date(effectiveFrom).toISOString(),
      effectiveTo: effectiveTo.trim() ? new Date(effectiveTo).toISOString() : null,
      isActiveFlag,
      conditions: conditions.filter((c) => c.value.trim()).map((c) => ({ ...c, value: c.value.trim() })),
    };

    const run = isEdit
      ? updateMutation.mutateAsync({ id: existing!.id, payload })
      : createMutation.mutateAsync(payload);

    run
      .then(() => {
        notification.success(isEdit ? "Configuration updated." : "Configuration created as Draft.");
        onClose();
      })
      .catch((e) => {
        const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
        notification.error(msg ?? "Failed to save configuration.");
      });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{isEdit ? "Edit points configuration" : "New points configuration"}</DialogTitle>
        <DialogDescription>
          Define how points are earned and redeemed. Saved as a Draft; submit it for approval before it takes effect.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <Field>
          <FieldLabel>Configuration name <span className="text-destructive">*</span></FieldLabel>
          <Input value={name} onChange={(e) => setName(e.target.value)} disabled={mutation.isPending}
            aria-invalid={!!errors.name} />
          {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
        </Field>

        {/* Earning */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Earning</p>
          <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 p-3 text-sm">
            <span>Earn</span>
            <Input
              type="number"
              step="1"
              min={0}
              className="w-20"
              value={earnRatePoints}
              onChange={(e) => setEarnRatePoints(e.target.value)}
              disabled={mutation.isPending}
              aria-label="Points earned"
              aria-invalid={!!errors.earnRatePoints}
            />
            <span>point(s) for every KES</span>
            <Input
              type="number"
              step="0.01"
              min={0}
              className="w-28"
              value={earnRatePerAmount}
              onChange={(e) => setEarnRatePerAmount(e.target.value)}
              disabled={mutation.isPending}
              aria-label="Per order amount in KES"
              aria-invalid={!!errors.earnRatePerAmount}
            />
            <span>of order value.</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Points are earned on the order amount. Use Conditions below to restrict which orders or
            customers qualify (e.g. customer group, product category).
          </p>
        </div>

        {/* Redemption */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Redemption</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
            <Field>
              <FieldLabel>Point value (1 point = KES) <span className="text-destructive">*</span></FieldLabel>
              <Input type="number" step="0.0001" min={0} value={pointValueKes}
                onChange={(e) => setPointValueKes(e.target.value)} disabled={mutation.isPending}
                aria-invalid={!!errors.pointValueKes} />
              {errors.pointValueKes && <p className="text-xs text-destructive">{errors.pointValueKes}</p>}
            </Field>
            <Field>
              <FieldLabel>Minimum redeemable points</FieldLabel>
              <Input type="number" step="1" min={0} value={minRedeemable}
                onChange={(e) => setMinRedeemable(e.target.value)} disabled={mutation.isPending}
                aria-invalid={!!errors.minRedeemable} />
            </Field>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
            <Field>
              <FieldLabel>Max redeemable per order (optional)</FieldLabel>
              <Input type="number" step="1" min={0} value={maxRedeemable}
                onChange={(e) => setMaxRedeemable(e.target.value)} disabled={mutation.isPending}
                aria-invalid={!!errors.maxRedeemable} />
              {errors.maxRedeemable && <p className="text-xs text-destructive">{errors.maxRedeemable}</p>}
            </Field>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
          <Field>
            <FieldLabel>Effective from <span className="text-destructive">*</span></FieldLabel>
            <Input type="date" value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)} disabled={mutation.isPending}
              aria-invalid={!!errors.effectiveFrom} />
            {errors.effectiveFrom && <p className="text-xs text-destructive">{errors.effectiveFrom}</p>}
          </Field>
          <Field>
            <FieldLabel>Effective to (optional)</FieldLabel>
            <Input type="date" value={effectiveTo}
              onChange={(e) => setEffectiveTo(e.target.value)} disabled={mutation.isPending}
              aria-invalid={!!errors.effectiveTo} />
            {errors.effectiveTo && <p className="text-xs text-destructive">{errors.effectiveTo}</p>}
          </Field>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={isActiveFlag} onCheckedChange={(v) => setIsActiveFlag(!!v)} disabled={mutation.isPending} />
          Active (rule applies once approved and in its effective window)
        </label>

        {/* Conditions */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Conditions (all must match)</p>
            <Button type="button" size="sm" variant="outline" onClick={addCondition} disabled={mutation.isPending}>
              Add condition
            </Button>
          </div>
          {conditions.length === 0 ? (
            <p className="text-xs text-muted-foreground">No conditions — the rule applies to all orders.</p>
          ) : (
            conditions.map((c, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                <Select value={c.conditionType} onValueChange={(v) => updateCondition(idx, { conditionType: v as PointsConditionType })} disabled={mutation.isPending}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONDITION_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={c.operator} onValueChange={(v) => updateCondition(idx, { operator: v as PointsConditionOperator })} disabled={mutation.isPending}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {OPERATORS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input placeholder="Value" value={c.value}
                  onChange={(e) => updateCondition(idx, { value: e.target.value })} disabled={mutation.isPending} />
                <Button type="button" size="sm" variant="ghost" onClick={() => removeCondition(idx)} disabled={mutation.isPending}>
                  ✕
                </Button>
              </div>
            ))
          )}
        </div>
      </div>

      <DialogFooter>
        <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={mutation.isPending || !isValid}>
          {mutation.isPending ? "Saving..." : isEdit ? "Save changes" : "Create draft"}
        </Button>
      </DialogFooter>
    </>
  );
};
